import { EditorPlatformContextEnvironment } from '@wix/editor-platform-environment-api';
import { ApplicationContext } from '@wix/editor-platform-contexts';
import { createErrorBuilder, BaseError } from '@wix/public-editor-platform-errors';
import { createHostModule } from '@wix/sdk-runtime/host-modules';
import { getMonitoringClientFunction } from '@wix/monitoring-browser-sdk-host';

new EditorPlatformContextEnvironment().expose();

class EditorPlatformSDKAuthError extends BaseError {
  state = {};
  constructor(message, code) {
    super(message, code, "Auth Strategy Error");
  }
  withAppDefinitionId(appDefinitionId) {
    this.state = { ...this.state, appDefinitionId };
    return this;
  }
}
const createEditorPlatformSDKAuthError = createErrorBuilder(EditorPlatformSDKAuthError);
function hasEnoughTimeUntilExpiry(expires, expiryBuffer) {
  const now = Date.now();
  const expiryTime = expires ? new Date(expires).getTime() : null;
  return expiryTime && expiryTime - now > expiryBuffer;
}
const ACCESS_TOKEN_EXPIRY_GRACE_PERIOD = 30 * 60 * 1e3;
const auth = () => {
  let instance;
  return {
    getAuthHeaders: async () => {
      const context = await ApplicationContext.getInstance();
      const appDefinitionId = context.getAppDefinitionId();
      const privateAPI = context.getPrivateAPI();
      if (!appDefinitionId) {
        throw createEditorPlatformSDKAuthError(
          "EmptyAppDefinitionId" /* EmptyAppDefinitionId */
        );
      }
      if (!instance || hasEnoughTimeUntilExpiry(
        instance.expires,
        ACCESS_TOKEN_EXPIRY_GRACE_PERIOD
      )) {
        instance = await privateAPI.auth.getAccessToken(
          appDefinitionId,
          await privateAPI.info.getMetaSiteId()
        );
      }
      if (!instance?.token) {
        console.error(
          createEditorPlatformSDKAuthError(
            "EmptyAppAuthInstance" /* EmptyAppAuthInstance */
          ).withAppDefinitionId(appDefinitionId)
        );
        return {
          headers: {
            Authorization: ""
          }
        };
      }
      return {
        headers: {
          Authorization: instance.token
        }
      };
    }
  };
};

class PlatformSDKShape {
  constructor(ShapeConstructor, selectedMethods = []) {
    this.ShapeConstructor = ShapeConstructor;
    this.selectedMethods = selectedMethods;
  }
  withPublicMethod(method) {
    return new PlatformSDKShape(this.ShapeConstructor, [
      ...this.selectedMethods,
      method
    ]);
  }
  build() {
    if (!this.selectedMethods.length) {
      throw new Error("methods are not defined for the SDK shape");
    }
    const sdk = this.selectedMethods.reduce(
      (acc, method) => {
        return {
          ...acc,
          [method]: (host) => {
            const instance = new this.ShapeConstructor(
              host?.applicationContext
            );
            if (!instance[method]) {
              return () => {
                console.error(`"${method}" is not defined in the SDK shape`);
              };
            }
            return instance[method].bind(instance);
          }
        };
      },
      {}
    );
    return createHostModule(sdk);
  }
}

class BaseSDKError extends BaseError {
  constructor(message, code) {
    super(message, code, "Base SDK Error");
  }
}
const createBaseSDKError = createErrorBuilder(
  BaseSDKError
);
class BaseSDKShape {
  constructor(overriddenApplicationContext = null) {
    this.overriddenApplicationContext = overriddenApplicationContext;
  }
  getApplicationContext() {
    return this.overriddenApplicationContext ? this.overriddenApplicationContext : ApplicationContext.getInstance();
  }
  async getAppDefinitionId() {
    return (await this.getApplicationContext()).getAppDefinitionId();
  }
  async getManifestContextId() {
    return (await this.getApplicationContext()).getManifestContextId();
  }
  async getRequiredManifestContextId() {
    const manifestContextId = await this.getManifestContextId();
    if (!manifestContextId) {
      throw createBaseSDKError("ManifestContextIdRequired" /* ManifestContextIdRequired */);
    }
    return manifestContextId;
  }
  async getEnvironmentContext() {
    return (await this.getApplicationContext()).getEnvironmentContext();
  }
  async getPlatformPrivateAPI() {
    return (await this.getApplicationContext()).getPrivateAPI();
  }
  // NOTE: don't remove this for now
  // protected dep<TDep extends IBaseSDKShape>(Factory: TDep) {
  //   let instance: InstanceType<TDep>;
  //
  //   return () => {
  //     if (!instance) {
  //       instance = new Factory(this.getApplicationContext());
  //     }
  //
  //     return instance;
  //   };
  // }
}

class ApplicationSDKShape extends BaseSDKShape {
  async getPrivateAPI() {
    return (await this.getApplicationContext()).getPrivateApplicationAPI();
  }
  async getPublicAPI(appDefinitionId) {
    const privateAPI = await this.getPlatformPrivateAPI();
    return privateAPI.applicationManager.getPublicApplicationAPI(
      appDefinitionId
    );
  }
  async getAppInstance() {
    const privateAPI = await this.getPlatformPrivateAPI();
    return privateAPI.info.getAppInstance(await this.getAppDefinitionId());
  }
}
var index$d = new PlatformSDKShape(ApplicationSDKShape).withPublicMethod("getPrivateAPI").withPublicMethod("getPublicAPI").withPublicMethod("getAppInstance").build();

class EventsSDKShape extends BaseSDKShape {
  async addEventListener(name, cb) {
    return (await this.getApplicationContext()).getEvents().addEventListener(name, cb);
  }
}
var index$c = new PlatformSDKShape(EventsSDKShape).withPublicMethod("addEventListener").build();

class ElementsSDKShape extends BaseSDKShape {
  async getSelection() {
    const privateAPI = await this.getPlatformPrivateAPI();
    const refs = await privateAPI.components.getSelectedComponents();
    return Promise.all(
      refs.map(
        (ref) => privateAPI.components.getComponent(ref)
      )
    );
  }
  async onSelectionChange(cb) {
    const events = new EventsSDKShape(this.getApplicationContext());
    void events.addEventListener("componentSelectionChanged", (payload) => {
      cb(payload.components);
    });
  }
}
var index$b = new PlatformSDKShape(ElementsSDKShape).withPublicMethod("onSelectionChange").withPublicMethod("getSelection").build();

class InfoSDKShape extends BaseSDKShape {
  async getViewMode() {
    const privateAPI = await this.getPlatformPrivateAPI();
    return privateAPI.info.getViewMode();
  }
  async getLanguageCode() {
    const privateAPI = await this.getPlatformPrivateAPI();
    return privateAPI.info.getLanguageCode();
  }
  async getSiteDirection() {
    const privateAPI = await this.getPlatformPrivateAPI();
    return privateAPI.info.getSiteDirection();
  }
  async siteHasCustomClasses() {
    const privateAPI = await this.getPlatformPrivateAPI();
    return privateAPI.info.siteHasCustomClasses();
  }
  async getThemeCustomProperties(filter) {
    const privateAPI = await this.getPlatformPrivateAPI();
    return privateAPI.info.getThemeCustomProperties(filter);
  }
}
var index$a = new PlatformSDKShape(InfoSDKShape).withPublicMethod("getViewMode").withPublicMethod("getLanguageCode").withPublicMethod("getSiteDirection").withPublicMethod("siteHasCustomClasses").withPublicMethod("getThemeCustomProperties").build();

var WidgetShapeErrorCode = /* @__PURE__ */ ((WidgetShapeErrorCode2) => {
  WidgetShapeErrorCode2["UndefinedCompRef"] = "UndefinedCompRef";
  WidgetShapeErrorCode2["NotAvailableMethod"] = "NotAvailableMethod";
  return WidgetShapeErrorCode2;
})(WidgetShapeErrorCode || {});
class WidgetShapeError extends BaseError {
  constructor(message, code) {
    super(message, code, "Widget Error");
  }
}
const createWidgetShapeError = createErrorBuilder(WidgetShapeError);

const UNDERLINE_DEFINITION = "underline";
function getFontFamiliesFromFonts(fonts) {
  return fonts.map((font) => parseFontString({ font }).family).filter(Boolean);
}
function parseFontString(fontPickerValue) {
  const font = {
    family: void 0,
    size: void 0
  };
  if (fontPickerValue?.font) {
    const { variableName, fallbackValue } = extractFontVar(
      fontPickerValue.font
    );
    font.cssVariableName = variableName;
    const parts = fallbackValue.match(/(?:(["']).*?\1|\S)+/g) || [];
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      switch (part) {
        case "bold":
          font.bold = true;
          break;
        case "italic":
          font.italic = true;
          break;
        default:
          if (part?.endsWith("px")) {
            font.size = parseInt(part, 10);
          } else if (i === parts.length - 1) {
            font.family = part;
          } else {
            font.family = (font.family || "") + " " + part;
          }
      }
    }
  }
  if (fontPickerValue?.textDecoration) {
    font.underline = fontPickerValue.textDecoration === UNDERLINE_DEFINITION;
  }
  font.family = font.family?.trim().replace(/['"]+/g, "");
  return font;
}
function extractFontVar(fontString) {
  const trimmedFont = fontString.trim();
  const match = trimmedFont.match(/var\((--[\w-]+),\s*(.+)\)/);
  if (match) {
    return {
      variableName: match[1],
      fallbackValue: match[2]?.trim() ?? ""
    };
  } else {
    return {
      variableName: void 0,
      fallbackValue: trimmedFont
    };
  }
}

class WidgetScopedSDK {
  constructor(compRef, privateAPI) {
    this.compRef = compRef;
    this.privateAPI = privateAPI;
  }
  async getProp(propName) {
    const props = await this.privateAPI.refComponents.getProps(this.compRef);
    return props?.[propName];
  }
  async setProp(propName, value) {
    await this.privateAPI.refComponents.setProps(this.compRef, {
      [propName]: value
    });
  }
  async getDesignPreset() {
    return this.privateAPI.designPresets.getDesignPresetName(this.compRef);
  }
  async setDesignPreset(designPresetName) {
    await this.privateAPI.designPresets.setDesignPresetByName(
      this.compRef,
      designPresetName
    );
  }
  async getNestedWidget(selector) {
    const childCompRef = await this.privateAPI.refComponents.findComponentBySelector(
      this.compRef,
      selector
    );
    if (!childCompRef) {
      return null;
    }
    return new WidgetScopedSDK(childCompRef, this.privateAPI);
  }
}
class WidgetSDKShape extends BaseSDKShape {
  async #getSelectedComponentRef() {
    const privateAPI = await this.getPlatformPrivateAPI();
    const refs = await privateAPI.components.getSelectedComponents();
    const compRef = refs[0];
    if (!compRef) {
      throw createWidgetShapeError(WidgetShapeErrorCode.UndefinedCompRef);
    }
    return compRef;
  }
  async setPreloadFonts(fonts) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const compRef = await this.#getSelectedComponentRef();
    if (await privateAPI.refComponents.isRefComponent(compRef)) {
      throw createWidgetShapeError(
        WidgetShapeErrorCode.NotAvailableMethod,
        "not available for the current component type"
      );
    }
    const fontFamilies = getFontFamiliesFromFonts(fonts);
    await privateAPI.customElement.setPreloadFonts(compRef, fontFamilies);
  }
  async getProp(propName) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const compRef = await this.#getSelectedComponentRef();
    if (await privateAPI.refComponents.isRefComponent(compRef)) {
      return new WidgetScopedSDK(compRef, privateAPI).getProp(propName);
    } else {
      return privateAPI.customElement.getAttribute(compRef, propName);
    }
  }
  async setProp(propName, value) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const compRef = await this.#getSelectedComponentRef();
    if (await privateAPI.refComponents.isRefComponent(compRef)) {
      return new WidgetScopedSDK(compRef, privateAPI).setProp(propName, value);
    } else {
      await privateAPI.customElement.setAttribute(compRef, propName, value);
    }
  }
  async getDesignPreset() {
    const privateAPI = await this.getPlatformPrivateAPI();
    const compRef = await this.#getSelectedComponentRef();
    if (!await privateAPI.refComponents.isRefComponent(compRef)) {
      throw createWidgetShapeError(
        WidgetShapeErrorCode.NotAvailableMethod,
        "not available for the current component type"
      );
    }
    return new WidgetScopedSDK(compRef, privateAPI).getDesignPreset();
  }
  async setDesignPreset(designPresetName) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const compRef = await this.#getSelectedComponentRef();
    if (!await privateAPI.refComponents.isRefComponent(compRef)) {
      throw createWidgetShapeError(
        WidgetShapeErrorCode.NotAvailableMethod,
        "not available for the current component type"
      );
    }
    return new WidgetScopedSDK(compRef, privateAPI).setDesignPreset(
      designPresetName
    );
  }
  async getNestedWidget(selector) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const compRef = await this.#getSelectedComponentRef();
    return new WidgetScopedSDK(compRef, privateAPI).getNestedWidget(selector);
  }
}
const widgetShape = new PlatformSDKShape(WidgetSDKShape).withPublicMethod("getNestedWidget").withPublicMethod("setDesignPreset").withPublicMethod("getDesignPreset").withPublicMethod("setProp").withPublicMethod("getProp").withPublicMethod("setPreloadFonts");
var index$9 = widgetShape.build();

function fontValueToCSS(fontValue) {
  return `${fontValue.cssVariableName ? `var(${fontValue.cssVariableName}, ` : ""}${fontValue.italic ? "italic " : ""}${fontValue.bold ? "bold " : ""}${fontValue.size || 16}px ${// wrap each font family with quotes
  fontValue.family?.split(",").map((fontFamily) => `"${fontFamily.replace(/['"]+/g, "")}"`).join(",") || "serif"}${fontValue.cssVariableName ? ")" : ""}`;
}
function parseColorString(colorPickerValue) {
  if (!colorPickerValue) {
    return null;
  }
  const colorString = colorPickerValue;
  const match = colorString.match(/var\((--[\w-]+),\s*(.+)\)/);
  if (match) {
    return {
      cssVariableName: match[1],
      color: match[2]
    };
  } else {
    return {
      color: colorString
    };
  }
}
function colorValueToCSS(colorValue) {
  if (!colorValue) {
    return "";
  }
  return colorValue.cssVariableName ? `var(${colorValue.cssVariableName}, ${colorValue.color})` : colorValue.color;
}
const fonts = {
  transformFontInternalValue: (value) => {
    if (value) {
      const { theme, cssVariableName, bold, italic, underline, ...rest } = value;
      return {
        ...rest,
        style: {
          bold,
          italic,
          underline
        },
        editorKey: cssVariableName || theme || ""
      };
    } else {
      return {
        editorKey: "font_7",
        family: "helvetica-w01-roman",
        size: 16,
        style: {}
      };
    }
  }
};
class InputsSDKShape extends BaseSDKShape {
  async selectColor(value, options) {
    const privateAPI = await this.getPlatformPrivateAPI();
    let colorValue = parseColorString(value);
    let colorResult = colorValueToCSS(colorValue);
    await privateAPI.inputs.openColorPicker(
      {
        color: colorValue?.cssVariableName || colorValue?.theme || colorValue?.color
      },
      ({
        color,
        theme,
        cssVariableTheme
      }) => {
        colorValue = {
          color,
          theme,
          cssVariableName: cssVariableTheme
        };
        colorResult = colorValueToCSS(colorValue);
        options?.onChange?.(colorResult);
      }
    );
    return colorResult;
  }
  async selectFont(value, options) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const fontValue = parseFontString(value);
    let _value = value;
    await privateAPI.inputs.openFontPickerV2(
      {
        ...options,
        panelSectionsDefinition: {
          htmlTag: "hidden"
        },
        componentStyle: fonts.transformFontInternalValue(fontValue)
      },
      (font, accessibility) => {
        _value = {
          font: fontValueToCSS(font),
          textDecoration: font.underline ? UNDERLINE_DEFINITION : void 0
        };
        options?.onChange?.(_value);
      }
    );
    return _value;
  }
}
var index$8 = new PlatformSDKShape(InputsSDKShape).withPublicMethod("selectColor").withPublicMethod("selectFont").build();

class PanelsSDKShape extends BaseSDKShape {
  async openLanguageSupportPanel() {
    const privateAPI = await this.getPlatformPrivateAPI();
    return privateAPI.panels.openLanguageSupportPanel();
  }
  async openFontsUploadPanel() {
    const privateAPI = await this.getPlatformPrivateAPI();
    return privateAPI.panels.openFontsUploadPanel();
  }
}
var index$7 = new PlatformSDKShape(PanelsSDKShape).withPublicMethod("openLanguageSupportPanel").withPublicMethod("openFontsUploadPanel").build();

class ModalsSDKShape extends BaseSDKShape {
  async openDashboardModal(options) {
    const privateAPI = await this.getPlatformPrivateAPI();
    return privateAPI.panels.openDashboardPanel(
      await this.getAppDefinitionId(),
      options
    );
  }
}
var index$6 = new PlatformSDKShape(ModalsSDKShape).withPublicMethod("openDashboardModal").build();

class ElementSDKShape extends BaseSDKShape {
  constructor(overriddenApplicationContext = null, childPath = []) {
    super(overriddenApplicationContext);
    this.overriddenApplicationContext = overriddenApplicationContext;
    this.childPath = childPath;
  }
  async getStyleDefinitions() {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.getStyleDefinitions(
      manifestContextId,
      this.childPath
    );
  }
  async getStyles() {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.getStyles(
      manifestContextId,
      this.childPath
    );
  }
  async setStyles({
    cssProperties,
    cssCustomProperties
  } = {}) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.setStyles(
      manifestContextId,
      this.childPath,
      {
        cssProperties,
        cssCustomProperties
      }
    );
  }
  async removeStyles(values = {}) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.removeStyles(
      manifestContextId,
      this.childPath,
      values
    );
  }
  async getDataDefinitions() {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.getDataDefinitions(
      manifestContextId,
      this.childPath
    );
  }
  async getData() {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.getData(
      manifestContextId,
      this.childPath
    );
  }
  async getResolvedData({ dataItemKey }) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.getResolvedData(
      manifestContextId,
      this.childPath,
      {
        dataItemKey
      }
    );
  }
  async setData(values) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.setData(
      manifestContextId,
      this.childPath,
      values
    );
  }
  async getPresetDefinitions() {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.getPresetDefinitions(
      manifestContextId,
      this.childPath
    );
  }
  async getAppliedPreset() {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.getAppliedPreset(
      manifestContextId,
      this.childPath
    );
  }
  async applyPreset(options) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.applyPreset(
      manifestContextId,
      this.childPath,
      options
    );
  }
  // TODO: group type should come from builder enum
  async getDisplayGroupDefinitions(options) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.getDisplayGroupDefinitions(
      manifestContextId,
      this.childPath,
      options
    );
  }
  async getState() {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.getState(
      manifestContextId,
      this.childPath
    );
  }
  async setState(stateKey) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.setState(
      manifestContextId,
      this.childPath,
      stateKey
    );
  }
  async getStateDefinitions() {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.getStateDefinitions(
      manifestContextId,
      this.childPath
    );
  }
  async onChange(cb) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.onContextualManifestChange(
      manifestContextId,
      cb
    );
  }
  async selectFont(params) {
    try {
      const privateAPI = await this.getPlatformPrivateAPI();
      const manifestContextId = await this.getRequiredManifestContextId();
      const selectedFont = await privateAPI.builderComponent.selectFont(
        manifestContextId,
        this.childPath,
        {
          ...params,
          showWeights: true
        }
      );
      return selectedFont;
    } catch (error) {
    }
  }
  async selectFontFamily(params) {
    try {
      const privateAPI = await this.getPlatformPrivateAPI();
      const manifestContextId = await this.getRequiredManifestContextId();
      const selectedFont = await privateAPI.builderComponent.selectFont(
        manifestContextId,
        this.childPath,
        {
          ...params,
          showWeights: false
        }
      );
      return selectedFont;
    } catch (error) {
    }
  }
  async selectMedia(params) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.selectMedia(
      manifestContextId,
      this.childPath,
      params
    );
  }
  async selectLink(params) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.selectLink(
      manifestContextId,
      this.childPath,
      params
    );
  }
  async selectColor(params) {
    const privateAPI = await this.getPlatformPrivateAPI();
    try {
      const manifestContextId = await this.getRequiredManifestContextId();
      const selectedColor = await privateAPI.builderComponent.selectColor(
        manifestContextId,
        this.childPath,
        params
      );
      return selectedColor;
    } catch (error) {
    }
  }
  async selectFontWeight(params) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.selectFontWeight(
      manifestContextId,
      this.childPath,
      params
    );
  }
  async selectTextTheme(params) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.selectTextTheme(
      manifestContextId,
      params
    );
  }
}
var index$5 = new PlatformSDKShape(ElementSDKShape).withPublicMethod("getDataDefinitions").withPublicMethod("getData").withPublicMethod("getResolvedData").withPublicMethod("setData").withPublicMethod("getStyleDefinitions").withPublicMethod("getStyles").withPublicMethod("setStyles").withPublicMethod("removeStyles").withPublicMethod("getPresetDefinitions").withPublicMethod("getAppliedPreset").withPublicMethod("applyPreset").withPublicMethod("getDisplayGroupDefinitions").withPublicMethod("getState").withPublicMethod("setState").withPublicMethod("onChange").withPublicMethod("getStateDefinitions").withPublicMethod("selectFont").withPublicMethod("selectFontFamily").withPublicMethod("selectMedia").withPublicMethod("selectLink").withPublicMethod("selectColor").withPublicMethod("selectFontWeight").withPublicMethod("selectTextTheme").build();

class PreferencesSDKShape extends BaseSDKShape {
  async get(keys) {
    const privateAPI = await this.getPlatformPrivateAPI();
    return privateAPI.userPreferences.get(
      await this.getAppDefinitionId(),
      keys
    );
  }
  async set(preferences) {
    const privateAPI = await this.getPlatformPrivateAPI();
    return privateAPI.userPreferences.set(
      await this.getAppDefinitionId(),
      preferences
    );
  }
}
var index$4 = new PlatformSDKShape(PreferencesSDKShape).withPublicMethod("set").withPublicMethod("get").build();

class ExternalPanelsSDKShape extends BaseSDKShape {
  async getData() {
    const privateAPI = await this.getPlatformPrivateAPI();
    return privateAPI.externalPanels.getData();
  }
  async setData(newData) {
    const privateAPI = await this.getPlatformPrivateAPI();
    return privateAPI.externalPanels.setData(newData);
  }
  async getStyle() {
    const privateAPI = await this.getPlatformPrivateAPI();
    return privateAPI.externalPanels.getStyle();
  }
  async setStyle(newStyle) {
    const privateAPI = await this.getPlatformPrivateAPI();
    return privateAPI.externalPanels.setStyle(newStyle);
  }
  async getTheme() {
    const privateAPI = await this.getPlatformPrivateAPI();
    return privateAPI.externalPanels.getTheme();
  }
  async selectMedia() {
    const privateAPI = await this.getPlatformPrivateAPI();
    return privateAPI.externalPanels.selectMedia();
  }
  async selectLink(...options) {
    const privateAPI = await this.getPlatformPrivateAPI();
    return privateAPI.externalPanels.selectLink(...options);
  }
  async selectColor(...options) {
    return async () => {
      const privateAPI = await this.getPlatformPrivateAPI();
      return privateAPI.externalPanels.selectColor(...options);
    };
  }
  async translate(key, values) {
    const privateAPI = await this.getPlatformPrivateAPI();
    return privateAPI.externalPanels.translate(key, values);
  }
}
var index$3 = new PlatformSDKShape(ExternalPanelsSDKShape).withPublicMethod("getTheme").withPublicMethod("getData").withPublicMethod("getStyle").withPublicMethod("selectColor").withPublicMethod("selectLink").withPublicMethod("selectMedia").withPublicMethod("setData").withPublicMethod("setStyle").withPublicMethod("translate").build();

var ControllersErrorCode = /* @__PURE__ */ ((ControllersErrorCode2) => {
  ControllersErrorCode2["UndefinedCompRef"] = "UndefinedCompRef";
  return ControllersErrorCode2;
})(ControllersErrorCode || {});
class ControllersError extends BaseError {
  constructor(message, code) {
    super(message, code, "Controllers Error");
  }
}
const createControllersError = createErrorBuilder(ControllersError);

class ControllersSDKShape extends BaseSDKShape {
  async #getSelectedComponentRef() {
    const privateAPI = await this.getPlatformPrivateAPI();
    const refs = await privateAPI.components.getSelectedComponents();
    const compRef = refs[0];
    if (!compRef) {
      throw createControllersError(ControllersErrorCode.UndefinedCompRef);
    }
    return compRef;
  }
  async get() {
    const privateAPI = await this.getPlatformPrivateAPI();
    return privateAPI.controllers.get(
      await this.getAppDefinitionId(),
      await this.#getSelectedComponentRef()
    );
  }
}
var index$2 = new PlatformSDKShape(ControllersSDKShape).withPublicMethod("get").build();

class BindingConnectionItem {
  constructor(privateAPI, compRef, bindingTargetId, config) {
    this.privateAPI = privateAPI;
    this.compRef = compRef;
    this.bindingTargetId = bindingTargetId;
    this.config = config;
  }
  update(data) {
    return this.privateAPI.bindings.update(this.compRef, this.bindingTargetId, {
      config: data.config,
      role: data.role
    });
  }
  delete() {
    return this.privateAPI.bindings.delete(this.compRef, this.bindingTargetId);
  }
}
class BindingsSDKShape extends BaseSDKShape {
  async get(bindingTarget) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const compRefs = await privateAPI.components.getSelectedComponents();
    if (compRefs.length !== 1) {
      return null;
    }
    const [compRef] = compRefs;
    const dataBindingConfig = privateAPI.bindings.get(
      bindingTarget.id,
      compRef
    );
    if (!dataBindingConfig) {
      return null;
    }
    return new BindingConnectionItem(
      privateAPI,
      compRef,
      bindingTarget.id,
      dataBindingConfig
    );
  }
}
var index$1 = new PlatformSDKShape(BindingsSDKShape).withPublicMethod("get").build();

const DATA_BINDING_PANEL_ID = "Data Binding";
const { dataBindingPanelId, bindingItemName } = {
  dataBindingPanelId: "",
  bindingItemName: "dataBinding"
};
class BindingsProvider {
  constructor(privateAPI, compRef, dataItemTarget, name, dataBindingPanelId2) {
    this.privateAPI = privateAPI;
    this.compRef = compRef;
    this.dataItemTarget = dataItemTarget;
    this.name = name;
    this.dataBindingPanelId = dataBindingPanelId2;
  }
  select() {
    return this.privateAPI.bindings.openDataBindingPanel(
      this.dataItemTarget.id,
      this.dataBindingPanelId
    );
  }
  createBinding(data) {
    const created = this.privateAPI.bindings.create(this.compRef, {
      dataItemId: this.dataItemTarget.id,
      bindingItemType: bindingItemName,
      ...data
    });
    if (!created) {
      return null;
    }
    return new BindingConnectionItem(
      this.privateAPI,
      this.compRef,
      this.dataItemTarget.id,
      JSON.stringify(data)
    );
  }
}
class BindingsProvidersSDKShape extends BaseSDKShape {
  async list(bindingTarget) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const compRefs = await privateAPI.components.getSelectedComponents();
    if (compRefs.length !== 1) {
      return [];
    }
    const [compRef] = compRefs;
    if (!await privateAPI.bindings.canCreateBindingsProvider(compRef)) {
      return [];
    }
    return [
      new BindingsProvider(
        privateAPI,
        compRef,
        bindingTarget,
        DATA_BINDING_PANEL_ID,
        dataBindingPanelId
      )
    ];
  }
}
var index = new PlatformSDKShape(BindingsProvidersSDKShape).withPublicMethod("list").build();

class Admin {
  constructor(applicationContext) {
    this.applicationContext = applicationContext;
  }
  async adminAPI() {
    const context = await this.applicationContext;
    const privateAPI = context.getPrivateAPI();
    const adminAPI = await privateAPI.admin;
    return adminAPI;
  }
  getContextIds = () => {
    throw new Error("Not implemented yet");
  };
  openItemSelector = (_args) => {
    throw new Error("Not implemented yet");
  };
}
class AdminHost {
  #api;
  getAPI() {
    return this.#api;
  }
  constructor(applicationContext) {
    this.#api = new Admin(applicationContext);
  }
}

const frame = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const essentialsQueryParamValue = queryParams.has("essentials") ? JSON.parse(queryParams.get("essentials")) : void 0;
  const { monitoring, ...essentials } = essentialsQueryParamValue || {};
  const appDefinitionId = queryParams.get("appDefinitionId");
  const { context, config } = monitoring || {};
  const getMonitoringClient = getMonitoringClientFunction({
    hostContext: { appId: appDefinitionId, ...context },
    monitoringConfig: config,
    sentrySDK: typeof window !== "undefined" ? window.Sentry : void 0
  });
  const applicationContext = ApplicationContext.getInstance();
  const adminHost = new AdminHost(applicationContext);
  return {
    environment: {},
    applicationContext,
    channel: {
      observeState: async () => {
        return { disconnect() {
        } };
      }
    },
    getMonitoringClient,
    admin: adminHost.getAPI(),
    essentials
  };
};
const worker = () => {
  const applicationContext = ApplicationContext.getInstance();
  const adminHost = new AdminHost(applicationContext);
  return {
    environment: {},
    applicationContext,
    channel: {
      observeState: async () => {
        return { disconnect() {
        } };
      }
    },
    admin: adminHost.getAPI()
  };
};

const editor = {
  host: () => {
    const isWorker = typeof importScripts === "function";
    if (isWorker) {
      return worker();
    }
    return frame();
  },
  auth
};

export { BaseSDKShape, ElementSDKShape, PlatformSDKShape, index$d as application, index$1 as bindings, index as bindingsProviders, index$2 as controllers, editor, index$5 as element, index$b as elements, index$c as events, index$3 as externalPanels, index$a as info, index$8 as inputs, index$6 as modals, index$7 as panels, index$4 as preferences, index$9 as widget };
//# sourceMappingURL=index.js.map
